import React, { useState } from 'react';
import { Functions, httpsCallable } from 'firebase/functions';
import './MenuOverrideModal.css';

interface Store {
  storeId?: string;
  name: string;
  appId: string;
}

interface MenuOverrideModalProps {
  store: Store;
  appId: string;
  functions: Functions;
  onSuccess: (message: string) => void;
  onError: (error: string) => void;
  onCancel: () => void;
  setLoading: (loading: boolean) => void;
}

const MenuOverrideModal: React.FC<MenuOverrideModalProps> = ({
  store,
  appId,
  functions,
  onSuccess,
  onError,
  onCancel,
  setLoading
}) => {
  const [menuFiles, setMenuFiles] = useState<File[]>([]);
  const [menuNames, setMenuNames] = useState<string[]>(['Main Menu']);
  const [newMenuName, setNewMenuName] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<'upload' | 'confirm'>('upload');
  const [processedMenuData, setProcessedMenuData] = useState<any>(null);
  const [inputMode, setInputMode] = useState<'files' | 'json'>('files');
  const [jsonInput, setJsonInput] = useState<string>('');

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const supportedFiles = Array.from(files).filter((file: File) => {
        const fileType = file.type.toLowerCase();
        const fileName = file.name.toLowerCase();
        
        return (
          fileType.startsWith('image/') ||
          fileType === 'application/pdf' ||
          fileType === 'text/csv' ||
          fileType === 'application/csv' ||
          fileType === 'application/vnd.ms-excel' ||
          fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          fileName.endsWith('.csv') ||
          fileName.endsWith('.xls') ||
          fileName.endsWith('.xlsx')
        );
      });
      
      if (supportedFiles.length > 0) {
        setMenuFiles(prev => [...prev, ...supportedFiles]);
      }
      
      if (supportedFiles.length !== files.length) {
        alert(`Some files were not added. Only images, PDFs, CSV, and Excel files are supported.`);
      }
    }
  };

  // Remove file
  const removeFile = (index: number) => {
    setMenuFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Menu name handlers
  const addMenuName = () => {
    if (newMenuName.trim()) {
      setMenuNames(prev => [...prev, newMenuName.trim()]);
      setNewMenuName('');
    }
  };

  const removeMenuName = (index: number) => {
    setMenuNames(prev => prev.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addMenuName();
    }
  };

  // Get file display info
  const getFileDisplayInfo = (file: File) => {
    const fileType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();
    
    if (fileType.startsWith('image/')) {
      return { type: 'Image', icon: '🖼️' };
    } else if (fileType === 'application/pdf') {
      return { type: 'PDF', icon: '📄' };
    } else if (fileType === 'text/csv' || fileType === 'application/csv' || fileName.endsWith('.csv')) {
      return { type: 'CSV', icon: '📊' };
    } else if (
      fileType === 'application/vnd.ms-excel' ||
      fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      fileName.endsWith('.xls') ||
      fileName.endsWith('.xlsx')
    ) {
      return { type: 'Excel', icon: '📈' };
    }
    return { type: 'File', icon: '📎' };
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const supportedFiles = Array.from(files).filter((file: File) => {
        const fileType = file.type.toLowerCase();
        const fileName = file.name.toLowerCase();
        
        return (
          fileType.startsWith('image/') ||
          fileType === 'application/pdf' ||
          fileType === 'text/csv' ||
          fileType === 'application/csv' ||
          fileType === 'application/vnd.ms-excel' ||
          fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          fileName.endsWith('.csv') ||
          fileName.endsWith('.xls') ||
          fileName.endsWith('.xlsx')
        );
      });
      
      if (supportedFiles.length > 0) {
        setMenuFiles(prev => [...prev, ...supportedFiles]);
      }

      if (supportedFiles.length !== files.length) {
        alert(`Some files were not added. Only images, PDFs, CSV, and Excel files are supported.`);
      }
    }
  };

  const handleUploadAreaClick = () => {
    const fileInput = document.getElementById('menu-override-files');
    if (fileInput) {
      fileInput.click();
    }
  };

  // Process JSON input directly (bypass AI)
  const processJsonInput = () => {
    if (!jsonInput.trim()) {
      setProcessingError('Please paste valid menu JSON data.');
      return;
    }

    setIsProcessing(true);
    setProcessingError('');

    try {
      // Parse and validate JSON
      const parsedData = JSON.parse(jsonInput);
      
      // Basic validation
      if (!parsedData.menu || !Array.isArray(parsedData.menu)) {
        throw new Error('Invalid menu data: "menu" array is required');
      }
      
      if (!parsedData.menuSettings || !Array.isArray(parsedData.menuSettings)) {
        throw new Error('Invalid menu data: "menuSettings" array is required');
      }

      console.log('📋 JSON Menu Data Validated:', parsedData);

      // Store the parsed data and move to confirmation step
      setProcessedMenuData(parsedData);
      setCurrentStep('confirm');

    } catch (error: any) {
      console.error('Error parsing JSON:', error);
      const errorMessage = error.message || 'Invalid JSON format. Please check your input.';
      setProcessingError(errorMessage);
      onError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  // Process menu files with Gemini (Step 1)
  const processMenu = async () => {
    if (menuFiles.length === 0 || menuNames.length === 0) {
      setProcessingError('Please upload at least one file and define at least one menu type.');
      return;
    }

    setIsProcessing(true);
    setProcessingError('');

    try {
      // Process menu files with Gemini
      const processMenuFunction = httpsCallable(functions, 'processMenuFiles');
      
      const fileData: any = {
        menuNames: menuNames.filter(name => name.trim() !== '')
      };

      // Convert all files to base64
      if (menuFiles.length > 0) {
        const filePromises = menuFiles.map(file => {
          return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        });
        fileData.images = await Promise.all(filePromises);
      }
      
      const processResult = await processMenuFunction(fileData);
      const processData = processResult.data as { success: boolean; menuData?: any; error?: string };
      
      if (!processData.success || !processData.menuData) {
        throw new Error(processData.error || 'Failed to process menu files');
      }

      console.log('🍽️ Gemini Menu Processing Result:', processData.menuData);

      // Store the processed data and move to confirmation step
      setProcessedMenuData(processData.menuData);
      setCurrentStep('confirm');

    } catch (error: any) {
      console.error('Error processing menu:', error);
      const errorMessage = error.message || 'Failed to process menu files. Please try again.';
      setProcessingError(errorMessage);
      onError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  // Confirm and override menu (Step 2)
  const confirmOverride = async () => {
    if (!processedMenuData) {
      setProcessingError('No processed menu data available.');
      return;
    }

    setIsProcessing(true);
    setProcessingError('');
    setLoading(true);

    try {
      // Call overwriteStoreMenu cloud function with correct payload structure
      const overwriteStoreMenuFunction = httpsCallable(functions, 'overwriteStoreMenu');
      
      const overwriteResult = await overwriteStoreMenuFunction({
        storeData: {
          storeId: store.storeId,
          name: store.name,
          menuData: processedMenuData
        },
        appId: appId
      });

      const overwriteData = overwriteResult.data as { 
        success: boolean; 
        message?: string; 
        storeId?: string;
        appId?: string;
        orgId?: string;
      };
      
      if (overwriteData.success) {
        onSuccess(overwriteData.message || `Menu updated successfully for ${store.name}!`);
        onCancel(); // Close modal
      } else {
        throw new Error('Failed to update menu');
      }

    } catch (error: any) {
      console.error('Error overriding menu:', error);
      const errorMessage = error.message || 'Failed to update menu. Please try again.';
      setProcessingError(errorMessage);
      onError(errorMessage);
    } finally {
      setIsProcessing(false);
      setLoading(false);
    }
  };

  const handleBackToUpload = () => {
    setCurrentStep('upload');
    setProcessingError('');
  };

  const isValid = inputMode === 'files' 
    ? (menuFiles.length > 0 && menuNames.length > 0)
    : jsonInput.trim().length > 0;

  return (
    <div className="mom-overlay">
      <div className="mom-container">
        <div className="mom-header">
          <h2 className="mom-header-title">
            {currentStep === 'upload' ? `Update Menu for ${store.name}` : 'Confirm Menu Update'}
          </h2>
          <button className="mom-close-button" onClick={onCancel} disabled={isProcessing}>
            ×
          </button>
        </div>

        {currentStep === 'upload' ? (
          <>
            <div className="mom-content">
              {/* Mode Toggle */}
              <div className="mom-mode-toggle">
                <button
                  type="button"
                  className={`mom-mode-btn ${inputMode === 'files' ? 'active' : ''}`}
                  onClick={() => setInputMode('files')}
                  disabled={isProcessing}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
                    <polyline points="13 2 13 9 20 9"/>
                  </svg>
                  Upload Files
                </button>
                <button
                  type="button"
                  className={`mom-mode-btn ${inputMode === 'json' ? 'active' : ''}`}
                  onClick={() => setInputMode('json')}
                  disabled={isProcessing}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 18 22 12 16 6"/>
                    <polyline points="8 6 2 12 8 18"/>
                  </svg>
                  Paste JSON
                </button>
              </div>

              <div className="mom-warning">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <p className="mom-warning-text">
                  {inputMode === 'files' 
                    ? 'Upload your menu files and we\'ll process them with AI before updating.'
                    : 'Paste your menu JSON data to update the menu directly without AI processing.'}
                </p>
              </div>

              {inputMode === 'files' ? (
                <div className="mom-form-columns">
                  {/* Left Column - Menu Types */}
                  <div className="mom-types-column">
                    <div className="mom-types-header">
                      <h3>Menu Types</h3>
                      <p>Define the different menus you want to process</p>
                    </div>
                    
                    <div className="mom-types-list">
                      {menuNames.map((name, index) => (
                        <div key={index} className="mom-type-item">
                          <div className="mom-type-content">
                            <span className="mom-type-name">{name || 'Unnamed Menu'}</span>
                            <span className="mom-type-index">#{index + 1}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeMenuName(index)}
                            className="mom-type-remove"
                            title="Remove menu type"
                            disabled={isProcessing}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="mom-add-section">
                      <div className="mom-add-input-group">
                        <input
                          type="text"
                          value={newMenuName}
                          onChange={(e) => setNewMenuName(e.target.value)}
                          onKeyPress={handleKeyPress}
                          placeholder="Enter menu name (e.g., Drinks, Desserts)"
                          className="mom-add-input"
                          disabled={isProcessing}
                        />
                        <button
                          type="button"
                          onClick={addMenuName}
                          disabled={!newMenuName.trim() || isProcessing}
                          className="mom-add-button"
                        >
                          Add
                        </button>
                      </div>
                      <small className="mom-add-hint">
                        e.g. Breakfast Menu, Lunch Menu, Kids Menu, Drinks Menu
                      </small>
                    </div>
                  </div>

                  {/* Right Column - File Upload */}
                  <div className="mom-upload-column">
                    <div className="mom-upload-container">
                      <input
                        id="menu-override-files"
                        type="file"
                        accept="image/*,.pdf,.csv,.xls,.xlsx,application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        multiple
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                        disabled={isProcessing}
                      />
                      
                      <div 
                        className="mom-upload-area"
                        onClick={handleUploadAreaClick}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                      >
                        <div className="mom-upload-icon">📎</div>
                        <p className="mom-upload-title">Click to upload menu files</p>
                        <p className="mom-upload-subtitle">or drag and drop files here</p>
                        <small className="mom-upload-formats">
                          Supports: Images, PDFs, CSV, Excel files
                        </small>
                      </div>
                    </div>

                    {menuFiles.length > 0 && (
                      <div className="mom-files-section">
                        <div className="mom-files-header">
                          <span>Uploaded Files ({menuFiles.length})</span>
                        </div>
                        <div className="mom-files-grid">
                          {menuFiles.map((file, index) => {
                            const { type, icon } = getFileDisplayInfo(file);
                            return (
                              <div key={index} className="mom-file-item">
                                {file.type.startsWith('image/') ? (
                                  <img 
                                    src={URL.createObjectURL(file)} 
                                    alt={`Menu ${index + 1}`}
                                    className="mom-file-preview-img"
                                  />
                                ) : (
                                  <div className="mom-file-icon-preview">
                                    <span className="mom-file-icon">{icon}</span>
                                    <span className="mom-file-type">{type}</span>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeFile(index)}
                                  className="mom-file-remove"
                                  title="Remove file"
                                  disabled={isProcessing}
                                >
                                  ×
                                </button>
                                <span className="mom-file-name">{file.name}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mom-json-input-section">
                  <div className="mom-json-header">
                    <h3>Paste Menu JSON</h3>
                    <p>Enter your menu data in JSON format with "menu" and "menuSettings" arrays</p>
                  </div>
                  <textarea
                    className="mom-json-textarea"
                    value={jsonInput}
                    onChange={(e) => setJsonInput(e.target.value)}
                    placeholder={`{\n  "menu": [\n    {\n      "id": "item-1",\n      "name": "Burger",\n      "price": 12.99,\n      ...\n    }\n  ],\n  "menuSettings": [\n    {\n      "categories": [\n        { "name": "Main Course", ... }\n      ]\n    }\n  ]\n}`}
                    disabled={isProcessing}
                  />
                  <div className="mom-json-info">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="16" x2="12" y2="12"/>
                      <line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                    <span>The JSON must include both "menu" and "menuSettings" arrays at the root level.</span>
                  </div>
                </div>
              )}

              {processingError && (
                <div className="mom-error">
                  {processingError}
                </div>
              )}
            </div>

            <div className="mom-footer">
              <button
                type="button"
                className="mom-cancel-button"
                onClick={onCancel}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                type="button"
                className="mom-override-button"
                onClick={inputMode === 'files' ? processMenu : processJsonInput}
                disabled={!isValid || isProcessing}
              >
                {isProcessing ? (
                  <>
                    <div className="mom-spinner" />
                    {inputMode === 'files' ? 'Processing Menu...' : 'Validating JSON...'}
                  </>
                ) : (
                  inputMode === 'files' ? 'Process Menu with AI' : 'Validate & Continue'
                )}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mom-content">
              <div className="mom-confirm-header">
                <div className="mom-confirm-icon">✅</div>
                <div>
                  <h3 className="mom-confirm-title">Menu Processed Successfully!</h3>
                  <p className="mom-confirm-subtitle">Review the details below before confirming the menu update</p>
                </div>
              </div>

              <div className="mom-confirm-stats">
                <div className="mom-stat-card">
                  <div className="mom-stat-icon">🍽️</div>
                  <div className="mom-stat-content">
                    <div className="mom-stat-value">{processedMenuData?.menu?.length || 0}</div>
                    <div className="mom-stat-label">Menu Items Detected</div>
                  </div>
                </div>

                <div className="mom-stat-card">
                  <div className="mom-stat-icon">📁</div>
                  <div className="mom-stat-content">
                    <div className="mom-stat-value">{processedMenuData?.menuSettings?.[0]?.categories?.length || 0}</div>
                    <div className="mom-stat-label">Categories Created</div>
                  </div>
                </div>

                <div className="mom-stat-card">
                  <div className="mom-stat-icon">📄</div>
                  <div className="mom-stat-content">
                    <div className="mom-stat-value">{inputMode === 'files' ? menuFiles.length : 1}</div>
                    <div className="mom-stat-label">{inputMode === 'files' ? 'Files Processed' : 'JSON Validated'}</div>
                  </div>
                </div>
              </div>

              {processedMenuData?.menuSettings?.[0]?.categories?.length > 0 && (
                <div className="mom-categories-preview">
                  <h4 className="mom-preview-title">Categories</h4>
                  <div className="mom-category-chips">
                    {processedMenuData.menuSettings[0].categories.map((category: any, index: number) => (
                      <div key={index} className="mom-category-chip">
                        {category.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mom-confirm-warning">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <p className="mom-warning-text">
                  Confirming will <strong>permanently replace</strong> your current menu with this new menu. 
                  This action cannot be undone.
                </p>
              </div>

              {processingError && (
                <div className="mom-error">
                  {processingError}
                </div>
              )}
            </div>

            <div className="mom-footer">
              <button
                type="button"
                className="mom-cancel-button"
                onClick={handleBackToUpload}
                disabled={isProcessing}
              >
                ← Back to Upload
              </button>
              <button
                type="button"
                className="mom-override-button"
                onClick={confirmOverride}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <div className="mom-spinner" />
                    Updating Menu...
                  </>
                ) : (
                  'Confirm & Update Menu'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MenuOverrideModal;